import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useAgenteDomanda,
  useAgenteDomande,
  type DomandaRiga,
  type EsitoDomanda,
} from "@/hooks/useAgenteDomande";
import { cn } from "@/lib/utils";

/** Dal peggiore al migliore: è l'ordine in cui vanno guardati. */
const ESITI: { key: EsitoDomanda; label: string; classe: string }[] = [
  { key: "mai_partita", label: "Mai partite", classe: "bg-red-100 text-red-800 border-red-200" },
  {
    key: "non_recapitata",
    label: "Non recapitate",
    classe: "bg-red-100 text-red-800 border-red-200",
  },
  {
    key: "non_capita",
    label: "Risposte non capite",
    classe: "bg-amber-100 text-amber-900 border-amber-200",
  },
  {
    key: "in_attesa",
    label: "In attesa",
    classe: "bg-slate-100 text-slate-700 border-slate-200",
  },
  {
    key: "chiusa_a_mano",
    label: "Chiuse a mano",
    classe: "bg-slate-100 text-slate-600 border-slate-200",
  },
  {
    key: "risolta",
    label: "Risolte",
    classe: "bg-emerald-100 text-emerald-800 border-emerald-200",
  },
];

const CLASSE_ESITO: Record<EsitoDomanda, string> = Object.fromEntries(
  ESITI.map((e) => [e.key, e.classe]),
) as Record<EsitoDomanda, string>;

function fmtData(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtOre(ore: number | null): string {
  if (ore == null) return "—";
  if (ore < 1) return `${Math.round(ore * 60)} min`;
  if (ore < 48) return `${Math.round(ore)} h`;
  return `${Math.round(ore / 24)} gg`;
}

export function AgenteDomande({ slug }: { slug: string }) {
  const [filtro, setFiltro] = useState<EsitoDomanda | null>(null);
  const [apertaId, setApertaId] = useState<string | null>(null);
  const { data, isLoading } = useAgenteDomande(slug, filtro ?? undefined);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!data) return <p className="text-sm text-muted-foreground">Domande non disponibili.</p>;

  const t = data.totali;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Domande" valore={t.totale} />
        <Kpi label="Con risposta" valore={`${t.percentuale_risposte}%`} />
        <Kpi label="Tempo mediano" valore={fmtOre(t.mediana_ore_risposta)} />
        <Kpi
          label="Risposte non capite"
          valore={t.per_esito.non_capita}
          allarme={t.per_esito.non_capita > 0}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Chip attivo={filtro === null} onClick={() => setFiltro(null)}>
          Tutte ({t.totale})
        </Chip>
        {ESITI.map((e) => (
          <Chip
            key={e.key}
            attivo={filtro === e.key}
            onClick={() => setFiltro(filtro === e.key ? null : e.key)}
          >
            {e.label} ({t.per_esito[e.key] ?? 0})
          </Chip>
        ))}
      </div>

      {data.domande.length === 0 ? (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          Nessuna domanda con questo filtro.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Chiesta</th>
                <th className="px-3 py-2 font-medium">Su cosa</th>
                <th className="px-3 py-2 font-medium">Cliente</th>
                <th className="px-3 py-2 font-medium">Tipo</th>
                <th className="px-3 py-2 font-medium">Esito</th>
                <th className="px-3 py-2 font-medium">Attesa / risposta</th>
              </tr>
            </thead>
            <tbody>
              {data.domande.map((d) => (
                <Riga
                  key={d.id}
                  d={d}
                  aperta={apertaId === d.id}
                  onToggle={() => setApertaId(apertaId === d.id ? null : d.id)}
                  slug={slug}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({
  label,
  valore,
  allarme,
}: {
  label: string;
  valore: string | number;
  allarme?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div
        className={cn(
          "mt-1 text-2xl font-bold tracking-tight tabular-nums",
          allarme && "text-amber-600",
        )}
      >
        {valore}
      </div>
    </div>
  );
}

function Chip({
  attivo,
  onClick,
  children,
}: {
  attivo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        attivo
          ? "border-primary bg-primary text-primary-foreground"
          : "bg-background hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

function Riga({
  d,
  aperta,
  onToggle,
  slug,
}: {
  d: DomandaRiga;
  aperta: boolean;
  onToggle: () => void;
  slug: string;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn("cursor-pointer border-b hover:bg-muted/40", aperta && "bg-muted/40")}
      >
        <td className="whitespace-nowrap px-3 py-2 tabular-nums">{fmtData(d.chiesta_il)}</td>
        <td className="px-3 py-2">{d.riferimento ?? "—"}</td>
        <td className="px-3 py-2">{d.cliente ?? "—"}</td>
        <td className="px-3 py-2 text-xs text-muted-foreground">{d.tipo_label}</td>
        <td className="px-3 py-2">
          <span
            className={cn(
              "rounded-full border px-2 py-0.5 text-[11px] font-medium",
              CLASSE_ESITO[d.esito],
            )}
          >
            {d.esito_label}
          </span>
        </td>
        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-xs">
          {d.risolta_il ? `risposta in ${fmtOre(d.ore_per_risposta)}` : `aperta da ${fmtOre(d.aperta_da_ore)}`}
        </td>
      </tr>
      {aperta && (
        <tr className="border-b bg-muted/20">
          <td colSpan={6} className="px-3 py-4">
            <Storia slug={slug} id={d.id} />
          </td>
        </tr>
      )}
    </>
  );
}

/** La storia della domanda: com'è partita, cosa ha risposto Marina, com'è andata. */
function Storia({ slug, id }: { slug: string; id: string }) {
  const { data, isLoading } = useAgenteDomanda(slug, id);
  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (!data) return <p className="text-sm text-muted-foreground">Dettaglio non disponibile.</p>;

  return (
    <div className="space-y-4 text-sm">
      <Blocco titolo="La domanda">
        {data.domanda ? (
          <>
            <div className="text-xs text-muted-foreground">
              {fmtData(data.domanda.inviata_il)} → {data.domanda.destinatari.join(", ") || "—"}
              {data.domanda.stato_invio ? ` · ${data.domanda.stato_invio}` : ""}
            </div>
            <div
              className="mt-2 rounded border bg-background p-3 text-sm [&_p]:mb-2"
              dangerouslySetInnerHTML={{ __html: data.domanda.corpo ?? "" }}
            />
            {data.domanda.errore && (
              <p className="mt-2 text-xs text-red-600">{data.domanda.errore}</p>
            )}
          </>
        ) : (
          <p className="text-muted-foreground">
            Nessun messaggio collegato: non sappiamo se e come sia stata posta, e nessuna
            risposta potrà chiuderla.
          </p>
        )}
        {data.campi.length > 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            Campi chiesti: <b>{data.campi.join(", ")}</b>
          </p>
        )}
        {data.opzioni.length > 0 && (
          <ol className="mt-2 list-decimal pl-5 text-xs text-muted-foreground">
            {data.opzioni.map((o, i) => (
              <li key={i}>{o.riferimento}</li>
            ))}
          </ol>
        )}
      </Blocco>

      <Blocco titolo={`Risposte di Marina (${data.tentativi.length})`}>
        {data.tentativi.length === 0 ? (
          <p className="text-muted-foreground">Nessuna risposta finora.</p>
        ) : (
          <ul className="space-y-2">
            {data.tentativi.map((t, i) => (
              <li key={i} className="rounded border bg-background p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{fmtData(t.il)}</span>
                  <span>·</span>
                  <span>{t.canale}</span>
                  {t.autore && <span>· {t.autore}</span>}
                  <span
                    className={cn(
                      "ml-auto rounded-full border px-2 py-0.5 text-[11px] font-medium",
                      t.capita
                        ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                        : "border-amber-200 bg-amber-100 text-amber-900",
                    )}
                  >
                    {t.capita ? "capita" : "non capita"}
                  </span>
                </div>
                {t.testo && <p className="mt-2 whitespace-pre-wrap">{t.testo}</p>}
                <p className="mt-1 text-xs text-muted-foreground">
                  {t.capita ? `Interpretata: ${t.interpretazione}` : t.motivo}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Blocco>
    </div>
  );
}

function Blocco({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {titolo}
      </h4>
      {children}
    </div>
  );
}
